import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { Pencil, Power } from 'lucide-react';
import {
  engagementInputSchema,
  type EngagementInput,
  type Paginated,
} from '@timesheet/shared';
import { useCrud } from '@/hooks/useCrud';
import { apiOrThrow } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Badge } from '@/components/ui/Badge';
import { CrudHeader } from '@/components/crud/CrudHeader';
import { BulkUpload } from '@/components/crud/BulkUpload';

interface EngagementRow {
  id: string;
  clientId: string;
  clientCode: string | null;
  clientName: string | null;
  code: string;
  name: string;
  fiscalYear: number;
  active: boolean;
  createdAt: string;
}

interface ClientOption {
  id: string;
  code: string;
  name: string;
}

const CURRENT_YEAR = new Date().getFullYear();

export function EngagementsPage() {
  const [search, setSearch] = useState('');
  const [yearFilter, setYearFilter] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<EngagementRow | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  // Need client list for the form's <select>.
  const clientsQuery = useQuery({
    queryKey: ['clients', 'options'],
    queryFn: () =>
      apiOrThrow<Paginated<ClientOption>>('/admin/clients?active=true&pageSize=200'),
  });

  const crud = useCrud<EngagementRow, EngagementInput>({
    basePath: '/admin/engagements',
    queryKey: ['engagements'],
    listParams: {
      active: 'all',
      q: search || undefined,
      fiscalYear: yearFilter || undefined,
    },
  });

  const columns: Column<EngagementRow>[] = [
    {
      key: 'client',
      header: 'Client',
      render: (r) => (
        <div>
          <div className="font-mono text-xs text-brand-gray-mid">{r.clientCode ?? '—'}</div>
          <div className="font-medium">{r.clientName ?? '—'}</div>
        </div>
      ),
    },
    { key: 'code', header: 'Code', className: 'font-mono font-medium' },
    { key: 'name', header: 'Engagement', grow: true },
    { key: 'fiscalYear', header: 'FY' },
    {
      key: 'active',
      header: 'Status',
      render: (r) => (
        <Badge tone={r.active ? 'success' : 'neutral'}>
          {r.active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <CrudHeader
        title="Engagements"
        description="Per-client projects scoped by fiscal year."
        searchValue={search}
        onSearchChange={setSearch}
        onCreate={() => setCreating(true)}
        createLabel="New engagement"
        extraActions={
          <>
            <Select
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              className="w-32"
            >
              <option value="">All years</option>
              {[CURRENT_YEAR + 1, CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2, CURRENT_YEAR - 3].map(
                (y) => (
                  <option key={y} value={y}>
                    FY {y}
                  </option>
                ),
              )}
            </Select>
            <Button variant="secondary" onClick={() => setBulkOpen(true)}>
              Bulk upload
            </Button>
          </>
        }
      />

      <DataTable
        columns={columns}
        rows={crud.list.data?.items}
        loading={crud.list.isPending}
        total={crud.list.data?.total}
        rowKey={(r) => r.id}
        actions={(r) => (
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={() => setEditing(r)}>
              <Pencil className="h-3.5 w-3.5" aria-hidden />
            </Button>
            {r.active && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (confirm(`Deactivate engagement "${r.code} — ${r.name}"?`)) {
                    crud.remove.mutate(r.id);
                  }
                }}
              >
                <Power className="h-3.5 w-3.5 text-brand-danger" aria-hidden />
              </Button>
            )}
          </div>
        )}
      />

      <EngagementFormModal
        key={creating ? 'new' : editing?.id ?? 'closed'}
        open={creating || !!editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSubmit={async (input) => {
          if (editing) {
            await crud.update.mutateAsync({ id: editing.id, input });
            setEditing(null);
          } else {
            await crud.create.mutateAsync(input);
            setCreating(false);
          }
        }}
        title={editing ? `Edit engagement — ${editing.code}` : 'New engagement'}
        defaultValues={
          editing
            ? {
                clientId: editing.clientId,
                code: editing.code,
                name: editing.name,
                fiscalYear: editing.fiscalYear,
                active: editing.active,
              }
            : undefined
        }
        clientOptions={clientsQuery.data?.items ?? []}
      />

      <BulkUpload
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title="Bulk upload engagements (CSV)"
        endpoint="/admin/bulk/engagements"
        invalidateKey={['engagements']}
        sampleHeaders={['clientCode', 'code', 'name', 'fiscalYear', 'active']}
      />
    </div>
  );
}

interface EngagementFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: EngagementInput) => Promise<void>;
  title: string;
  defaultValues?: Partial<EngagementInput>;
  clientOptions: ClientOption[];
}

function EngagementFormModal({
  open,
  onClose,
  onSubmit,
  title,
  defaultValues,
  clientOptions,
}: EngagementFormModalProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EngagementInput>({
    resolver: zodResolver(engagementInputSchema),
    defaultValues: {
      clientId: '',
      code: '',
      name: '',
      fiscalYear: CURRENT_YEAR,
      active: true,
      ...defaultValues,
    },
  });

  const submit = handleSubmit(async (values) => {
    try {
      await onSubmit(values);
    } catch (e) {
      alert((e as Error).message);
    }
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button onClick={submit} loading={isSubmitting} type="button">
            Save
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Client" error={errors.clientId?.message} required>
          {(p) => (
            <Select {...p} {...register('clientId')}>
              <option value="">— select client —</option>
              {clientOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {c.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Engagement code" error={errors.code?.message} required>
          {(p) => <Input {...p} {...register('code')} placeholder="AUDIT-2025-Q1" />}
        </Field>
        <Field label="Name" error={errors.name?.message} required>
          {(p) => <Input {...p} {...register('name')} placeholder="Statutory audit FY2025" />}
        </Field>
        <Field label="Fiscal year" error={errors.fiscalYear?.message} required>
          {(p) => (
            <Input
              {...p}
              type="number"
              min={2000}
              max={2100}
              {...register('fiscalYear', { valueAsNumber: true })}
            />
          )}
        </Field>
        <Checkbox label="Active" {...register('active')} defaultChecked />
      </form>
    </Modal>
  );
}
